/**
 * Daily-quest engine.
 *
 *   - `pickDailyQuests(dateISO)`  — deterministically picks N quests for a
 *      given date, sampled from `QUEST_POOL` with `weight`-biased selection.
 *      Same date → same quest set, so refreshes mid-day don't surprise the
 *      child. Each type appears at most once per day.
 *
 *   - `applyQuestEvent(quests, event)` — pure reducer. Given a list of
 *      active quests + an event { type, count? }, returns the next quest
 *      list with progress incremented and `completed` flagged on any quest
 *      that just hit its target. Also returns the array of quest ids that
 *      JUST completed so the caller can grant XP rewards.
 *
 * The store layer is responsible for rolling quests over when the day
 * changes — this module is pure.
 */

import { QUEST_POOL, QUESTS_PER_DAY, WEEKLY_REVIEW_QUEST } from '../data/questPool.js';
import { isSchoolDay, isFriday } from './dateUtils.js';

/**
 * Cheap deterministic 32-bit hash from a string. Same algorithm as the one
 * in ExerciseRouter so behaviour is predictable for tests.
 */
function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}

function seededRng(seed) {
  let h = hashString(String(seed));
  return () => {
    h = (h * 1664525 + 1013904223) >>> 0;
    return h / 0xffffffff;
  };
}

/**
 * Materialise a dynamic quest template into a concrete picked quest for
 * today, clamping its target to the available count.
 *
 * Returns null when the dynamic quest cannot be completed (zero available
 * items), in which case the caller filters it out of the candidate pool.
 */
function materialiseDynamicQuest(template, context) {
  const key = template.availableContextKey;
  const available = Number.isFinite(context?.[key]) ? context[key] : 0;
  if (available <= 0) return null;
  const cap = Math.max(1, template.targetCap ?? 5);
  const target = Math.min(cap, Math.max(1, Math.floor(available)));
  const xpRewardPerStep = template.xpRewardPerStep ?? 0;
  const xpReward = target * xpRewardPerStep;
  // Strip dynamic-only metadata from the picked quest so it serialises as
  // an ordinary quest with a concrete target/xpReward. The label-rendering
  // helper interpolates the actual `target` via labelVars.
  // eslint-disable-next-line no-unused-vars
  const { dynamic, targetCap, xpRewardPerStep: _xrps, availableContextKey, ...rest } =
    template;
  return {
    ...rest,
    target,
    xpReward,
    labelVars: { n: target },
  };
}

/**
 * Pick N quests for the given date. Greedy weighted sampling that avoids
 * picking two quests of the same `type` on the same day.
 *
 * `dateISO` is expected as `YYYY-MM-DD`. Any string works for testing.
 *
 * `context` carries live state needed to materialise dynamic quests. Today
 * the only field used is `availableWrongCount` — the number of items
 * currently in the wrong pool. Dynamic quests whose required context is
 * zero/missing are excluded from the candidate pool entirely so the child
 * never sees a quest they can't complete.
 */
export function pickDailyQuests(
  dateISO,
  count = QUESTS_PER_DAY,
  pool = QUEST_POOL,
  context = {}
) {
  if (!Array.isArray(pool) || pool.length === 0) return [];
  const rng = seededRng(`quest::${dateISO}`);
  // Pre-filter: dynamic quests with no available items are removed before
  // any sampling so the seeded weights stay consistent.
  const remaining = pool.filter((q) => {
    if (!q.dynamic) return true;
    const key = q.availableContextKey;
    const available = Number.isFinite(context?.[key]) ? context[key] : 0;
    return available > 0;
  });
  const picked = [];
  const seenTypes = new Set();

  while (picked.length < count && remaining.length > 0) {
    const eligible = remaining.filter((q) => !seenTypes.has(q.type));
    const candidates = eligible.length > 0 ? eligible : remaining;
    const totalWeight = candidates.reduce(
      (sum, q) => sum + (q.weight ?? 1),
      0
    );
    let roll = rng() * totalWeight;
    let chosen = candidates[candidates.length - 1];
    for (const q of candidates) {
      roll -= q.weight ?? 1;
      if (roll <= 0) {
        chosen = q;
        break;
      }
    }

    let concrete;
    if (chosen.dynamic) {
      concrete = materialiseDynamicQuest(chosen, context);
      // Defensive: if materialisation failed despite the pre-filter,
      // drop this template and re-sample.
      if (!concrete) {
        const idx = remaining.findIndex((q) => q.id === chosen.id);
        if (idx >= 0) remaining.splice(idx, 1);
        continue;
      }
    } else {
      concrete = { ...chosen };
    }

    picked.push({
      ...concrete,
      progress: 0,
      completed: false,
      rewardClaimed: false,
    });
    seenTypes.add(chosen.type);
    const idx = remaining.findIndex((q) => q.id === chosen.id);
    if (idx >= 0) remaining.splice(idx, 1);
  }
  return picked;
}

/**
 * Build the full quest slice for a given calendar date. This is the SINGLE
 * source of truth for "what daily mission(s) does this day have", shared by
 * the live rollover (progressStore) and the make-up archive synthesis
 * (dailyQuestArchive) so the two can never drift apart.
 *
 * Rules:
 *   - Saturday / Sunday          → no missions (rest day) → empty slice.
 *   - Friday (with history)      → ONLY the weekly review mission, which
 *                                  replaces the normal set.
 *   - Mon–Thu (or a Friday for a
 *     brand-new child with no
 *     history yet to review)     → the normal random set of QUESTS_PER_DAY.
 *
 * `context` shape:
 *   { availableWrongCount?: number, hasHistory?: boolean }
 * `availableWrongCount` materialises the dynamic mistakes_cleared quest;
 * `hasHistory` gates the Friday review (we never hand out "replay words" when
 * there's nothing recorded to replay).
 */
export function buildDailyQuestSlice(dateISO, context = {}) {
  if (!isSchoolDay(dateISO)) {
    return { date: dateISO, quests: [] };
  }
  if (isFriday(dateISO) && context.hasHistory) {
    return {
      date: dateISO,
      quests: [
        {
          ...WEEKLY_REVIEW_QUEST,
          progress: 0,
          completed: false,
          rewardClaimed: false,
        },
      ],
    };
  }
  return {
    date: dateISO,
    quests: pickDailyQuests(dateISO, undefined, undefined, {
      availableWrongCount: context.availableWrongCount ?? 0,
    }),
  };
}

/**
 * Count items currently in the wrong pool — drives the dynamic
 * `mistakes_cleared` quest. Pure helper.
 */
export function countWrongPool(itemTracking) {
  if (!itemTracking || typeof itemTracking !== 'object') return 0;
  let n = 0;
  for (const rec of Object.values(itemTracking)) {
    if (rec && rec.wrongPool === true) n += 1;
  }
  return n;
}

/**
 * Apply an event to the quest list.
 *
 * Event shape:
 *   { type: 'new_words' | 'correct_first' | 'perfect_session' | ...,
 *     count?: number = 1 }
 *
 * Returns:
 *   { quests: <next list>, justCompleted: <id[]> }
 */
export function applyQuestEvent(quests, event) {
  if (!Array.isArray(quests) || quests.length === 0) {
    return { quests: [], justCompleted: [] };
  }
  if (!event || typeof event.type !== 'string') {
    return { quests, justCompleted: [] };
  }
  const inc = Math.max(1, event.count ?? 1);
  const justCompleted = [];

  const next = quests.map((q) => {
    if (q.completed || q.type !== event.type) return q;
    const nextProgress = Math.min(q.target, q.progress + inc);
    const nowCompleted = nextProgress >= q.target;
    if (nowCompleted) justCompleted.push(q.id);
    return {
      ...q,
      progress: nextProgress,
      completed: nowCompleted,
    };
  });

  return { quests: next, justCompleted };
}

/**
 * Total XP that would be awarded if every quest in the list completed.
 * Used for the "potential XP today" display on the home page.
 */
export function totalQuestXp(quests) {
  if (!Array.isArray(quests)) return 0;
  return quests.reduce((sum, q) => sum + (q.xpReward ?? 0), 0);
}

/**
 * XP from the quests that have completed but not yet been claimed. The
 * store grants this and marks each quest `rewardClaimed = true`.
 */
export function pendingQuestXp(quests) {
  if (!Array.isArray(quests)) return 0;
  return quests
    .filter((q) => q.completed && !q.rewardClaimed)
    .reduce((sum, q) => sum + (q.xpReward ?? 0), 0);
}
